import { ChangeDetectorRef, Component, computed, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { Table } from 'primeng/table';
import { Select } from 'primeng/select';
import { DecimalPipe } from '@angular/common';

import { BAService } from 'app/ba/ba.service';
import { BA_InvoiceVm } from 'app/shared/generated/BA/Models/BA_InvoiceVm';
import { GlobalService } from 'app/shared/services/global.service';
import { SelectItem } from 'app/shared/models/select-item';
import { SharedTableModule } from 'app/shared/standalone/shared-table/shared-table.module';
import { AvailableColumns, VisibleColsService } from 'app/shared/standalone/shared-table/visible-cols.service';
import { LazyTableInput } from 'app/shared/standalone/shared-table/lazy-table-input.interface';
import { LazyTableService } from 'app/shared/standalone/shared-table/lazy-table.service';
import { LazyLoadVm } from 'app/shared/standalone/shared-table/lazy-load-vm.interface';

@Component({
    selector: 'pcg-budget-invoice-list'
    , templateUrl: './budget-invoice-list.component.html'
    , styleUrls: ['./budget-invoice-list.component.scss']
    , standalone: true
    , providers: [ DecimalPipe ]
    , host: { '(window:resize)': 'onResize()' }
    , changeDetection: ChangeDetectionStrategy.Eager
    , imports: [
        SharedTableModule
        , Select
    ]
})
export class BudgetInvoiceListComponent implements OnInit {

    baService = inject(BAService);
    route = inject(ActivatedRoute);
    router = inject(Router);
    httpClient = inject(HttpClient);
    decimalPipe = inject(DecimalPipe);
    lts = inject(LazyTableService);
    cdr = inject(ChangeDetectorRef);

    budgetId = signal<number>(0);
    poId = signal<number>(0);
    invoices = signal<BA_InvoiceVm[]>([]);
    newInvoiceUrl = computed(() => `/ba/budgets/invoices/edit/0/${this.budgetId()}`);

    isMobile = false;
    isLoading: boolean = true;

    columnDefs = BA_InvoiceVm.ColumnDefs;
    lazyLoadVm: LazyLoadVm;
    searchValue: string | undefined;
    filterInvoiceStatus: SelectItem[] = [];
    filterBaPOs: SelectItem[] = [];
    availableColumns!: AvailableColumns[];
    visibleColumns!: AvailableColumns[];
    searchColumns!: string[];

    filters = {
		baInvoiceStatus: null
        , poId: null
        , budgetId: null
	};
	initFilters = Object.assign({}, this.filters);

    tableDts = {
        root: {
            header: {
                lgPadding: "0 0 0.5rem 0"
            }
        }
    };

    lazyTableInput: LazyTableInput = {
        pageNumber: 1
        , pageSize: 100
        , sortField: "invoiceId"
        , sortOrder: 1
    };

    customExcelUrl = "api/BA/GetBudgetInvoiceListExcel";

    constructor(title: Title) { title.setTitle("Budget Invoices"); }

    onResize() { this.isMobile = GlobalService.setIsMobile(window.innerWidth); }

    ngOnInit() {
        this.isMobile = GlobalService.setIsMobile(window.innerWidth); 
        this.budgetId.set(this.route.snapshot.params.id);
        this.baService.budgetSignal()?.budgetId == this.budgetId() 
            ? null 
            : this.baService.getBudget(this.budgetId());
        this.filters.budgetId = this.budgetId();
        this.initFilters.budgetId = this.budgetId();
        var poid = this.route.snapshot.params.poId;
        if (poid) { 
            this.poId.set(poid);
            this.filters.poId = poid; 
            this.initFilters.poId = poid;
        }
        this.getFilters();
        this.getList();
    }

    getFilters() {
        this.baService.getInvoiceStatus().subscribe((lst: any)=> { this.filterInvoiceStatus = lst; });
        this.baService.getBaPOs(this.budgetId()).subscribe((lst: any)=> { 
            this.filterBaPOs = lst; 
            this.filterBaPOs.unshift({
                value: null
                , text: "All"
            });
        });
    }

    getList() {
        this.baService.getInvoices(this.filters.baInvoiceStatus, this.filters.poId, this.filters.budgetId)
            .subscribe((list: BA_InvoiceVm[]) => { 
                this.invoices.set(list);
                this.availableColumns = VisibleColsService.getAvailableCols(this.columnDefs);
                this.visibleColumns = this.availableColumns.filter(o => o.isVisible);
                this.searchColumns = this.availableColumns.filter(o => o.canSearch).map(o => o.field);
                this.isLoading = false;
                this.setExcelUrl();
            }
        );
    }

    columnChange(event: any) {
        this.visibleColumns = event;
        this.visibleColumns = event.sort((a, b) => {
            return this.availableColumns.findIndex(p => p.field === a.field) 
                - this.availableColumns.findIndex(p => p.field === b.field);
        });
    }

    clearFilters(
        table: Table
        , el: any
    ) {
        table.clear();
        this.searchValue = '';
        el.value = "";
        this.filters = Object.assign(this.filters, this.initFilters);
        this.getList();
    }

    setExcelUrl() {
		this.customExcelUrl = `api/BA/GetBudgetInvoiceListExcel` 
            + `?budgetId=${this.filters.budgetId ?? null}` 
            + `&poId=${this.filters.poId ?? null}`
            + `&baInvoiceStatus=${this.filters.baInvoiceStatus ?? null}`;
	}

    createInvoice() { this.router.navigate([`/ba/budgets/invoices/edit/0/${this.budgetId()}`]); }
    exportList() { this.httpClient.get(this.customExcelUrl).subscribe((output: any) => { window.open(output); }); }
    editInvoice(invoiceId: number) { this.router.navigateByUrl(`ba/budgets/invoices/edit/${invoiceId}/${this.budgetId()}`); }
}
